import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCategories } from '../hooks/useProducts';
import { Guitar, Piano, Drum, Mic, Headphones } from 'lucide-react';

const CategorySection: React.FC = () => {
  const { categories, loading, error } = useCategories();

  if (loading || error || categories.length === 0) {
    return null;
  }

  // Map category IDs to appropriate icons
  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case 'guitars':
        return <Guitar className="h-12 w-12 text-white" />;
      case 'keyboards':
        return <Piano className="h-12 w-12 text-white" />;
      case 'drums':
        return <Drum className="h-12 w-12 text-white" />;
      case 'studio':
        return <Mic className="h-12 w-12 text-white" />;
      case 'accessories':
        return <Headphones className="h-12 w-12 text-white" />;
      default:
        return <Guitar className="h-12 w-12 text-white" />;
    }
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-black mb-4">Shop by Category</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore our comprehensive collection of musical instruments and accessories
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Link
                to={`/products/${category.id}`}
                className="group block bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
              >
                <div className="relative h-56 bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                  {/* Category Image */}
                  {category.image_url && (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="absolute inset-0 w-full h-full object-cover opacity-30"
                    />
                  )}
                  
                  {/* Icon Overlay */}
                  <div className="relative z-10 group-hover:scale-110 transition-transform duration-300 shadow-icon-shadow">
                    {getCategoryIcon(category.id)}
                  </div>
                </div>
                
                <div className="p-4 text-center">
                  <h3 
                    className="font-black text-lg text-gray-900 group-hover:text-red-700 transition-colors"
                    style={{ textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)' }}
                  >
                    {category.name}
                  </h3>
                  {category.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {category.description}
                    </p>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategorySection;